import type {
  CreateBookingInput,
  CreateBookingResponse,
  PublicBooking,
  PublicEventType,
  PublicHost,
  RefundPreview,
  SlotsResponse,
} from '@bb/shared';
import { queryOptions, useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export function publicProfileQuery(username: string) {
  return queryOptions({
    queryKey: ['public', username],
    queryFn: () =>
      api<{ host: PublicHost; eventTypes: PublicEventType[] }>(`/v1/public/${username}`),
  });
}

export function publicEventTypeQuery(username: string, slug: string) {
  return queryOptions({
    queryKey: ['public', username, slug],
    queryFn: () =>
      api<{ host: PublicHost; eventType: PublicEventType }>(`/v1/public/${username}/${slug}`),
  });
}

export function useSlots(
  username: string,
  slug: string,
  from: string,
  to: string,
  tz: string,
  inviteCode?: string,
) {
  return useQuery({
    queryKey: ['public', username, slug, 'slots', from, to, tz, inviteCode ?? ''],
    queryFn: () =>
      api<SlotsResponse>(
        `/v1/public/${username}/${slug}/slots?from=${from}&to=${to}&tz=${encodeURIComponent(tz)}` +
          (inviteCode ? `&inviteCode=${encodeURIComponent(inviteCode)}` : ''),
      ),
    staleTime: 10_000,
  });
}

export function useHold(username: string, slug: string) {
  return useMutation({
    mutationFn: (input: { start: string; inviteCode?: string; releaseHoldId?: string }) =>
      api<{ holdId: string; expiresAt: string }>(`/v1/public/${username}/${slug}/hold`, {
        method: 'POST',
        body: input,
      }),
  });
}

/** Gives a held slot back, so the booker (or anyone else) can take it again. */
export function useReleaseHold(username: string, slug: string) {
  return useMutation({
    mutationFn: (input: { holdId: string; inviteCode?: string }) =>
      api<{ released: boolean }>(`/v1/public/${username}/${slug}/hold/release`, {
        method: 'POST',
        body: input,
      }),
  });
}

export interface InviteResolution {
  state: 'ok' | 'used' | 'expired' | 'unknown';
  username: string | null;
  host: PublicHost | null;
  eventType: PublicEventType | null;
  prefill: { name: string | null; email: string | null };
}

/** Opens a single-use booking link. Never throws for a spent or unknown code. */
export const inviteQuery = (code: string) =>
  queryOptions({
    queryKey: ['public', 'invite', code],
    queryFn: () => api<InviteResolution>(`/v1/public/invite/${encodeURIComponent(code)}`),
    staleTime: 0,
  });

/** Asks for a one-time code, when the host verifies booker addresses. */
export function useSendEmailCode(username: string, slug: string) {
  return useMutation({
    mutationFn: (input: { email: string; locale: string }) =>
      api<{ sent: boolean }>(`/v1/public/${username}/${slug}/verify-email`, {
        method: 'POST',
        body: input,
      }),
  });
}

export function useCreateBooking(username: string, slug: string) {
  return useMutation({
    mutationFn: (input: CreateBookingInput) =>
      api<CreateBookingResponse>(`/v1/public/${username}/${slug}/bookings`, {
        method: 'POST',
        body: input,
      }),
  });
}

export function publicBookingQuery(token: string) {
  return queryOptions({
    queryKey: ['public', 'booking', token],
    queryFn: () => api<PublicBooking>(`/v1/public/bookings/${token}`),
  });
}

export function useRefundPreview(token: string, enabled: boolean) {
  return useQuery({
    queryKey: ['public', 'booking', token, 'refund'],
    queryFn: () => api<RefundPreview>(`/v1/public/bookings/${token}/refund-preview`),
    enabled,
  });
}

export function useCancelBooking(token: string) {
  return useMutation({
    mutationFn: (reason: string) =>
      api<PublicBooking>(`/v1/public/bookings/${token}/cancel`, {
        method: 'POST',
        body: { reason },
      }),
  });
}

export function useRescheduleBooking(token: string) {
  return useMutation({
    mutationFn: (start: string) =>
      api<{ token: string; booking: PublicBooking }>(`/v1/public/bookings/${token}/reschedule`, {
        method: 'POST',
        body: { start },
      }),
  });
}
